<template>
  <div class="relative">
    <div class="flex items-center space-x-3 bg-background border border-gray-600 rounded-2xl px-4 py-3">
      <!-- Text Input -->
      <div class="flex-1">
        <input
          v-model="message"
          ref="inputRef"
          type="text"
          data-testid="chat-input"
          :placeholder="props.allowEmpty 
            ? $t('aiChat.placeholderOptional')
            : $t('aiChat.askAnything')"
          class="w-full bg-transparent text-white placeholder-gray-400 border-none outline-none focus:ring-0 text-base"
          :disabled="disabled"
          @keydown="handleKeydown"
          @compositionstart="handleCompositionStart"
          @compositionend="handleCompositionEnd"
        />
      </div>

      <!-- Skip Button (only show when allowEmpty is true) -->
      <button
        v-if="allowEmpty"
        type="button"
        data-testid="skip-button"
        @click="skipField"
        :disabled="disabled"
        class="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>{{ $t('aiChat.skip') }}</span>
        <Icon name="ph:arrow-right" class="w-4 h-4" />
      </button>

      <!-- Send Button -->
      <button 
        type="button"
        data-testid="send-button"
        @click="sendMessage"
        :disabled="!canSend"
        class="flex-shrink-0 p-2 text-gray-900 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Icon name="ph:paper-plane-right" class="w-4 h-4 text-white" />
      </button>
    </div>

    <!-- Optional Field Hint -->
    <Transition name="fade">
      <div 
        v-if="allowEmpty" 
        data-testid="hint-text"
        class="flex items-center gap-1 text-xs text-gray-400 mt-2 ml-1"
      >
        <Icon name="ph:info" class="w-3 h-3" />
        <span>{{ $t('aiChat.optionalFieldHint') }}</span>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, computed } from 'vue'

// Props
const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  allowEmpty: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['send'])

// Reactive data
const message = ref('')
const inputRef = ref(null)
const isComposing = ref(false)

// Computed: 判斷是否可以發送
const canSend = computed(() => {
  if (props.allowEmpty) {
    // 如果允許空值，只要不是 disabled 就可以發送
    return !props.disabled
  }
  // 否則需要有內容才能發送
  return !props.disabled && message.value.trim().length > 0
})

// Auto-focus when input becomes enabled
watch(() => props.disabled, (newVal) => {
  if (!newVal) {
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
})

// Methods
const handleCompositionStart = () => {
  isComposing.value = true
}

const handleCompositionEnd = () => {
  isComposing.value = false
}

const handleKeydown = (e) => {
  if (e.key === 'Enter' && !isComposing.value) {
    e.preventDefault()
    sendMessage()
  }
}

const sendMessage = () => {
  // 如果允許空值，或者有內容，就發送
  if (props.allowEmpty || message.value.trim()) {
    emit('send', message.value)
    message.value = ''
    nextTick(() => {
      inputRef.value?.focus()
    })
  }
}

const skipField = () => {
  // Skip 按鈕：發送空字串
  emit('send', '')
  message.value = ''
  nextTick(() => {
    inputRef.value?.focus()
  })
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
